import { useMemo, useState } from "react";
import {
	listAppleTeams,
	type AppleCertificateKind,
	type AppleProfile,
	type AppleTeam,
	createLimrunSecretStore,
	ensureAppleCertificateSecret,
	listAppleProfiles,
	saveAppleProfileSecret,
} from "@limrun/apple-auth";
import { useAppleIDLogin } from "@limrun/apple-auth/react";
import { Check, CheckCircle2, Loader } from "lucide-react";

import { transformUrl } from "@/auth/auth";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type AppleSigningDialogProps = {
	registryApiUrl?: string;
	registryToken?: string;
	organizationId?: string;
	/** Called after credentials landed in the secret store, even partially. */
	onSecretsSaved?: () => void;
};

/** The extracted auth package normalizes every portal team to one teamId. */
function teamSelectionId(team?: AppleTeam) {
	return team?.teamId || undefined;
}

type Step = "signin" | "verify" | "select" | "done";

const STEPS: Array<{ id: Step; label: string }> = [
	{ id: "signin", label: "Sign in" },
	{ id: "verify", label: "Verify" },
	{ id: "select", label: "Save" },
];

/**
 * Tell the user where Apple sent the code: the challenge body carries the
 * trusted phone's last two digits when Apple fell back to SMS.
 */
function twoFactorHint(challengeBody: unknown): string {
	if (typeof challengeBody === "object" && challengeBody !== null) {
		const body = challengeBody as Record<string, unknown>;
		const phone = (body.phoneNumber ?? body.trustedPhoneNumber) as Record<string, unknown> | undefined;
		const lastTwo = phone && typeof phone.lastTwoDigits === "string" ? phone.lastTwoDigits : undefined;
		if (lastTwo) {
			return `Enter the 6-digit code Apple sent to your phone number ending in ${lastTwo}. If several codes arrived, use the most recent one.`;
		}
	}
	return "Enter the 6-digit code shown on one of your trusted Apple devices.";
}

export function AppleSigningDialog({
	registryApiUrl,
	registryToken,
	organizationId,
	onSecretsSaved,
}: AppleSigningDialogProps) {
	const backendApiUrl = transformUrl(import.meta.env.VITE_API_ENDPOINT as string);
	const appleLogin = useAppleIDLogin({
		registryApiUrl: registryApiUrl ?? "",
		token: registryToken,
		organizationId,
	});

	const [open, setOpen] = useState(false);
	const [account, setAccount] = useState("");
	const [password, setPassword] = useState("");
	const [twoFactorCode, setTwoFactorCode] = useState("");
	const [teams, setTeams] = useState<AppleTeam[]>([]);
	const [selectedTeamId, setSelectedTeamId] = useState("");
	const [certificateKind, setCertificateKind] = useState<AppleCertificateKind>("development");
	const [profiles, setProfiles] = useState<AppleProfile[]>([]);
	const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>([]);
	const [busy, setBusy] = useState<string>();
	const [error, setError] = useState<string>();
	const [savedSecrets, setSavedSecrets] = useState<string[]>([]);

	const secretStore = useMemo(() => {
		if (!backendApiUrl || !registryToken || !organizationId) return undefined;
		return createLimrunSecretStore({
			apiUrl: backendApiUrl,
			token: registryToken,
			organizationId,
		});
	}, [backendApiUrl, registryToken, organizationId]);

	const relay = appleLogin.session?.relay;
	const authenticated = appleLogin.status === "authenticated";
	// Tracked locally instead of derived from appleLogin.status so a wrong
	// code (hook status becomes "error") keeps the user on the verify step.
	const [awaitingCode, setAwaitingCode] = useState(false);
	const step: Step =
		savedSecrets.length > 0 && !error ? "done"
		: authenticated ? "select"
		: awaitingCode ? "verify"
		: "signin";

	const handleOpenChange = (next: boolean) => {
		setOpen(next);
		if (!next) {
			void appleLogin.close();
			setTeams([]);
			setSelectedTeamId("");
			setProfiles([]);
			setSelectedProfileIds([]);
			setSavedSecrets([]);
			setError(undefined);
			setPassword("");
			setTwoFactorCode("");
			setAwaitingCode(false);
			setCertificateKind("development");
		}
	};

	function startOver() {
		void appleLogin.close();
		setAwaitingCode(false);
		setTwoFactorCode("");
		setPassword("");
		setError(undefined);
	}

	// The login session is passed explicitly instead of read back from hook
	// state: right after startLogin resolves, React has not re-rendered yet
	// and appleLogin.session would still be undefined.
	async function loadTeams(session: NonNullable<typeof appleLogin.session>) {
		setBusy("teams");
		setError(undefined);
		try {
			// The App Store Connect session cookie is minted by finalize; team
			// listing fails without it on some account types.
			await session.finalize().catch(() => undefined);
			const loaded = await listAppleTeams({ relay: session.relay });
			setTeams(loaded);
			const firstTeamId = loaded.map(teamSelectionId).find(Boolean) ?? "";
			setSelectedTeamId(firstTeamId);
			if (firstTeamId) await loadProfiles(firstTeamId, session.relay);
		} catch (caught) {
			setError(errorMessage(caught, "Failed to load Apple teams"));
		} finally {
			setBusy(undefined);
		}
	}

	async function loadProfiles(teamId: string, relayClient = relay) {
		if (!relayClient || !teamId) return;
		const loaded = await listAppleProfiles({ relay: relayClient, teamId });
		setProfiles(loaded);
		setSelectedProfileIds([]);
	}

	async function signIn() {
		setError(undefined);
		setBusy("login");
		try {
			const session = await appleLogin.startLogin({ accountName: account, password });
			if (session && !session.requiresTwoFactor) {
				setPassword("");
				await loadTeams(session);
			} else if (session) {
				setAwaitingCode(true);
			}
		} catch (caught) {
			setError(errorMessage(caught, "Apple sign-in failed"));
		} finally {
			setBusy(undefined);
		}
	}

	async function submitTwoFactor() {
		const session = appleLogin.session;
		if (!session) return;
		setError(undefined);
		setBusy("2fa");
		try {
			const response = await appleLogin.submitTwoFactorCode(twoFactorCode);
			if (response) {
				setTwoFactorCode("");
				setPassword("");
				setAwaitingCode(false);
				await loadTeams(session);
			}
		} catch (caught) {
			setError(errorMessage(caught, "Two-factor verification failed"));
		} finally {
			setBusy(undefined);
		}
	}

	async function selectTeam(teamId: string) {
		setSelectedTeamId(teamId);
		setError(undefined);
		setBusy("profiles");
		try {
			await loadProfiles(teamId);
		} catch (caught) {
			setError(errorMessage(caught, "Failed to load provisioning profiles"));
		} finally {
			setBusy(undefined);
		}
	}

	async function saveCredentials() {
		if (!relay || !selectedTeamId || !secretStore) return;
		setError(undefined);
		setBusy("save");
		const saved: string[] = [];
		try {
			const certificate = await ensureAppleCertificateSecret({
				relay,
				teamId: selectedTeamId,
				secretStore,
				certificateKind,
			});
			saved.push(
				certificate.created
					? `Created certificate ${certificate.certificateId} and saved it as ${certificate.secret.name}`
					: `Reused stored certificate ${certificate.certificateId}`,
			);
			for (const profileId of selectedProfileIds) {
				const secret = await saveAppleProfileSecret({
					relay,
					teamId: selectedTeamId,
					profileId,
					secretStore,
				});
				saved.push(`Saved provisioning profile ${secret.name}`);
			}
			setSavedSecrets(saved);
			onSecretsSaved?.();
		} catch (caught) {
			// Report partial progress alongside the failure so the user knows
			// what already landed in the secret store.
			setSavedSecrets(saved);
			if (saved.length > 0) {
				onSecretsSaved?.();
			}
			const message = errorMessage(caught, "Failed to save Apple credentials");
			const capHit =
				/current (Development|Distribution) certificate|pending certificate request|Maximum number of certificates/i.test(
					message,
				);
			const cap = certificateKind === "distribution" ? "distribution certificates at 3" : "development certificates at 2";
			setError(
				capHit
					? `${message} — Apple caps ${cap}. Revoke one at developer.apple.com or reuse a stored certificate.`
					: message,
			);
		} finally {
			setBusy(undefined);
		}
	}

	function toggleProfile(profileId: string) {
		setSelectedProfileIds((current) =>
			current.includes(profileId) ? current.filter((id) => id !== profileId) : [...current, profileId],
		);
	}

	const configured = !!registryApiUrl && !!secretStore;

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button variant="outline" disabled={!configured}>
					Connect Apple Developer
				</Button>
			</DialogTrigger>
			<DialogContent className="max-h-[90vh] max-w-lg">
				<DialogHeader>
					<DialogTitle>Apple signing credentials</DialogTitle>
					<DialogDescription>
						Save a development certificate and provisioning profiles to your organization's secret store.
						Your Apple password never leaves this browser.
					</DialogDescription>
				</DialogHeader>

				<StepIndicator step={step} />

				{(error ?? appleLogin.error) && (
					<Alert variant="destructive">
						<AlertTitle>Something went wrong</AlertTitle>
						<AlertDescription className="break-words">{error ?? appleLogin.error}</AlertDescription>
					</Alert>
				)}

				{step === "signin" && (
					<div className="space-y-4">
						<label className="block space-y-2">
							<Label>Apple ID</Label>
							<Input
								type="email"
								autoComplete="username"
								placeholder="you@example.com"
								value={account}
								onChange={(event) => setAccount(event.currentTarget.value)}
							/>
						</label>
						<label className="block space-y-2">
							<Label>Password</Label>
							<Input
								type="password"
								autoComplete="current-password"
								value={password}
								onChange={(event) => setPassword(event.currentTarget.value)}
							/>
						</label>
						<Button
							className="w-full"
							onClick={() => void signIn()}
							disabled={!account || !password || busy === "login"}
						>
							{busy === "login" ? (
								<>
									<Loader className="mr-2 h-4 w-4 animate-spin" />
									Signing in...
								</>
							) : (
								"Sign in with Apple"
							)}
						</Button>
					</div>
				)}

				{step === "verify" && (
					<div className="space-y-4">
						<p className="text-sm text-muted-foreground">
							{twoFactorHint(appleLogin.twoFactorChallengeResponse?.body)}
						</p>
						<Input
							className="text-center text-lg tracking-[0.5em]"
							inputMode="numeric"
							autoComplete="one-time-code"
							autoFocus
							maxLength={6}
							placeholder="123456"
							value={twoFactorCode}
							onChange={(event) => setTwoFactorCode(event.currentTarget.value.replace(/\D/g, ""))}
							onKeyDown={(event) => {
								if (event.key === "Enter" && twoFactorCode.length === 6 && busy !== "2fa") {
									void submitTwoFactor();
								}
							}}
						/>
						<Button
							className="w-full"
							onClick={() => void submitTwoFactor()}
							disabled={twoFactorCode.length !== 6 || busy === "2fa"}
						>
							{busy === "2fa" ? (
								<>
									<Loader className="mr-2 h-4 w-4 animate-spin" />
									Verifying...
								</>
							) : (
								"Verify"
							)}
						</Button>
						<Button variant="ghost" className="w-full" onClick={startOver} disabled={busy === "2fa"}>
							Start over
						</Button>
					</div>
				)}

				{step === "select" && (
					<div className="space-y-4">
						<label className="block space-y-2">
							<Label>Team</Label>
							<select
								className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
								value={selectedTeamId}
								disabled={teams.length === 0 || !!busy}
								onChange={(event) => void selectTeam(event.currentTarget.value)}
							>
								<option value="">{busy === "teams" ? "Loading teams..." : "Select a team"}</option>
								{teams.map((team, index) => {
									const value = teamSelectionId(team) ?? `team-${index}`;
									return (
										<option key={value} value={value}>
											{team.name ?? value}
										</option>
									);
								})}
							</select>
						</label>

						<label className="block space-y-2">
							<Label>Certificate type</Label>
							<select
								className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
								value={certificateKind}
								disabled={busy === "save"}
								onChange={(event) => setCertificateKind(event.currentTarget.value as AppleCertificateKind)}
							>
								<option value="development">Development</option>
								<option value="distribution">Distribution (Ad Hoc &amp; App Store)</option>
							</select>
							<p className="text-xs text-muted-foreground">
								Ad Hoc and App Store builds are both signed with the Distribution certificate.
							</p>
						</label>

						<div className="space-y-2">
							<Label>Provisioning profiles to save</Label>
							{busy === "profiles" ? (
								<p className="text-sm text-muted-foreground">Loading profiles...</p>
							) : profiles.length === 0 ? (
								<p className="text-sm text-muted-foreground">
									No provisioning profiles on this team. Only the certificate will be saved; profiles
									created later can be added from this dialog again.
								</p>
							) : (
								<div className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
									{profiles.map((profile) => {
										const profileId = profile.profileId;
										const profileName = profile.name ?? profileId;
										return (
											<label
												key={profileId}
												className="flex min-w-0 cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted"
											>
												<input
													type="checkbox"
													className="shrink-0"
													checked={selectedProfileIds.includes(profileId)}
													onChange={() => toggleProfile(profileId)}
												/>
												<span className="min-w-0 flex-1 truncate" title={profileName}>
													{profileName}
												</span>
											</label>
										);
									})}
								</div>
							)}
						</div>

						{savedSecrets.length > 0 && (
							<div className="space-y-1 rounded-md border border-green-600/30 bg-green-600/10 p-3 text-sm">
								{savedSecrets.map((line) => (
									<div key={line} className="flex items-start gap-2">
										<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
										<span className="min-w-0 break-words">{line}</span>
									</div>
								))}
							</div>
						)}

						<Button
							className="w-full"
							onClick={() => void saveCredentials()}
							disabled={!selectedTeamId || busy === "save"}
						>
							{busy === "save" ? (
								<>
									<Loader className="mr-2 h-4 w-4 animate-spin" />
									Saving to secret store...
								</>
							) : (
								"Save credentials to secret store"
							)}
						</Button>
					</div>
				)}

				{step === "done" && (
					<div className="space-y-4">
						<div className="space-y-1 rounded-md border border-green-600/30 bg-green-600/10 p-3 text-sm">
							{savedSecrets.map((line) => (
								<div key={line} className="flex items-start gap-2">
									<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
									<span className="min-w-0 break-words">{line}</span>
								</div>
							))}
						</div>
						<div className="flex gap-2">
							<Button variant="outline" className="flex-1" onClick={() => setSavedSecrets([])}>
								Save more
							</Button>
							<Button className="flex-1" onClick={() => handleOpenChange(false)}>
								Done
							</Button>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}

function StepIndicator({ step }: { step: Step }) {
	const currentIndex = step === "done" ? STEPS.length : STEPS.findIndex((entry) => entry.id === step);
	return (
		<div className="flex items-center gap-2">
			{STEPS.map((entry, index) => {
				const completed = index < currentIndex;
				const active = index === currentIndex;
				return (
					<div key={entry.id} className="flex flex-1 items-center gap-2">
						<div
							className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium ${
								completed
									? "bg-primary text-primary-foreground"
									: active
										? "border-2 border-primary text-primary"
										: "border text-muted-foreground"
							}`}
						>
							{completed ? <Check className="h-3.5 w-3.5" /> : index + 1}
						</div>
						<span className={`text-xs ${active ? "font-medium" : "text-muted-foreground"}`}>
							{entry.label}
						</span>
						{index < STEPS.length - 1 && <div className="h-px flex-1 bg-border" />}
					</div>
				);
			})}
		</div>
	);
}

function errorMessage(error: unknown, fallback: string) {
	return error instanceof Error ? error.message : fallback;
}
